import type { StreamSlot } from "../types/stream";
import { isQueueType, kindOf, slotFallbackItem, type PlayableItem } from "../player";
import { linkUrlOf } from "./linkPlayer";

/** room-stc queue item types (spec 2026-10-19 §3.1): every item of the post, in post order. Which ones the
 * client takes is the player registry's (`isQueueType`). */
export type CastItemType = "PHOTO" | "YOUTUBE" | "SOUNDCLOUD" | "AUDIO_URL" | "RADIO_STREAM";
export const isCastItemType = (v: unknown): v is CastItemType => isQueueType(v);

export interface CastQueueItem {
  url: string;
  type: CastItemType;
  /** The link provider (lower case, spec 2026-10-24); left out when none (a photo, an unresolved row). */
  provider?: string;
  /** The codec hint; left out when unknown. */
  codec?: string;
  live?: boolean;
  externalId: string | null;
  title: string | null;
  thumbnailUrl: string | null;
}

/** room-stc Queue: the snapshot a link slot plays (a post's items, or one pasted link) and its current item. */
export interface CastQueue {
  postId: string | null;
  index: number;
  count: number;
  items: CastQueueItem[];
}

/** A stored queue and the rev it came with (null: from GET queue, which carries none). */
export interface QueueEntry {
  queue: CastQueue;
  rev: number | null;
}

const str = (v: unknown): string | null => (typeof v === "string" && v !== "" ? v : null);

function parseItem(v: unknown): CastQueueItem | null {
  if (typeof v !== "object" || v === null) return null;
  const o = v as Record<string, unknown>;
  const url = str(o.url);
  // `type` since spec 2026-10-19; older servers sent only `provider` (YOUTUBE | SOUNDCLOUD).
  const type = isCastItemType(o.type) ? o.type : isCastItemType(o.provider) ? o.provider : null;
  if (url == null || type == null) return null;
  const item: CastQueueItem = { url, type, externalId: str(o.externalId), title: str(o.title), thumbnailUrl: str(o.thumbnailUrl) };
  // Until 2026-10-24 `provider` repeated the type (upper case); now it is the link provider (lower case).
  const provider = str(o.provider);
  if (provider != null && provider === provider.toLowerCase()) item.provider = provider;
  const codec = str(o.codec);
  if (codec != null) item.codec = codec;
  if (typeof o.live === "boolean") item.live = o.live;
  return item;
}

/** A Queue from an event, an answer or GET queue; null when missing or malformed (old servers send none). */
export function parseQueue(v: unknown): CastQueue | null {
  if (typeof v !== "object" || v === null) return null;
  const o = v as Record<string, unknown>;
  if (!Array.isArray(o.items) || o.items.length === 0) return null;
  const items = o.items.map(parseItem);
  if (items.some((i) => i == null)) return null;
  const index = o.index;
  if (typeof index !== "number" || !Number.isInteger(index) || index < 0 || index >= items.length) return null;
  // count is the item count by contract; trust the items when the two disagree.
  return { postId: str(o.postId), index, count: items.length, items: items as CastQueueItem[] };
}

export const currentItemOf = (q: CastQueue): CastQueueItem => q.items[q.index];
export const upNextOf = (q: CastQueue): CastQueueItem[] => q.items.slice(q.index + 1);

/** The VMPL_LINK slot's media columns for an item (room-stc writes the same on the slot row, spec §3.1). */
export function slotMediaOf(item: CastQueueItem): Pick<StreamSlot, "mediaUrl" | "mediaType" | "youtubeVideoId"> {
  const slot = kindOf(item).slot;
  return {
    mediaUrl: item.url,
    mediaType: slot?.mediaType,
    youtubeVideoId: slot?.videoIdOnSlot ? (item.externalId ?? undefined) : undefined,
  };
}

/** The slot's facts about its current item (cast events since 2026-10-24: `itemKind`, `provider`, `codec`, `live`). */
export function slotItemFactsOf(item: CastQueueItem): Pick<StreamSlot, "itemKind" | "provider" | "codec" | "live"> {
  return { itemKind: item.type, provider: item.provider, codec: item.codec, live: item.live };
}

/**
 * What a link cast plays now, for the player registry. The queue knows the item; else the slot's own facts (cast
 * events); else only its media column, where MUSIC is either an audio file or a radio stream: without the queue it
 * plays live (no seek), which is safe for both. HTTP slot lists carry no facts: joining mid-cast reads the queue.
 */
export function linkItemOf(slot: StreamSlot, queue: CastQueue | null): PlayableItem {
  const url = linkUrlOf(slot);
  if (queue && url) {
    const current = queue.items[queue.index];
    if (current?.url === url) return current;
    const any = queue.items.find((i) => i.url === url);
    if (any) return any;
  }
  if (slot.itemKind) return { type: slot.itemKind, provider: slot.provider, codec: slot.codec, live: slot.live, url, externalId: slot.youtubeVideoId };
  return { ...slotFallbackItem(slot.mediaType), url, externalId: slot.youtubeVideoId };
}


/**
 * The caster's player follows its own `next` answer before the event arrives: when `entry` is newer
 * than the slot (both revs known), the slot plays the entry's current item.
 */
export function withQueueItem(slot: StreamSlot, entry: QueueEntry | undefined): StreamSlot {
  if (!entry || entry.rev == null || typeof slot.timelineRev !== "number" || entry.rev <= slot.timelineRev) return slot;
  const item = currentItemOf(entry.queue);
  const media = slotMediaOf(item);
  return slot.mediaUrl === media.mediaUrl ? slot : { ...slot, ...media, ...slotItemFactsOf(item) };
}
